import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { Fragment, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
import cn from '@/utils/merge-class-names';
import type { ModalAction, ModalProps } from './modal.type';

const Modal = forwardRef<ModalAction, ModalProps>(
  (
    {
      children,
      clickOutsideToClose,
      className,
      contentClassName,
      fullScreen,
      initialFocus,
      onClose,
      defaultOpen = false,
    },
    ref,
  ) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const handleOutsideClick = () => {
      if (!clickOutsideToClose) return;

      setIsOpen(false);
      onClose?.();
    };

    useEventListener(
      'contextmenu',
      (e) => {
        if (dialogRef.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      dialogRef,
    );

    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    }));

    return (
      <Transition
        appear
        show={isOpen}
        as={Fragment}>
        <Dialog
          static
          as='div'
          ref={dialogRef}
          className='relative z-50'
          onClose={handleOutsideClick}
          initialFocus={initialFocus}>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'>
            <div className='fixed inset-0 bg-black/75 backdrop-blur-sm' />
          </TransitionChild>

          <div className='fixed inset-0 overflow-y-auto'>
            <div
              className={cn(
                'flex min-h-full flex-col items-center justify-center p-4 text-center',
                { 'p-0': fullScreen },
                className,
              )}>
              <TransitionChild
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'>
                <DialogPanel
                  className={cn(
                    'w-[20rem] max-w-md overflow-hidden rounded-2xl bg-white px-6 py-2 text-left align-middle shadow-modal transition-all',
                    { 'p-0 max-w-none rounded-none': fullScreen },
                    contentClassName,
                  )}>
                  {children}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  },
);

Modal.displayName = 'Modal';

export default Modal;
